/**
 * Responsibility: draw the level curves of a field across one tile.
 *
 * ## Why a contour tracer earns its place
 *
 * A terraced vineyard is rows of vines following the hillside, and "following the hillside" is not a
 * decoration — it is the definition. Farmland approximates it by sampling the gradient once per field and
 * laying straight rows across it, which is right for a 520-unit field of wheat and wrong here: a vineyard
 * terrace *curves*, and the curve is the thing you recognise from the air.
 *
 * A level curve of the elevation field is exactly that curve, and it comes with three properties that would
 * each otherwise need code:
 *
 * 1. **It closes and continues by itself.** Rows wrap around a spur and pinch into a gully because the
 *    contour does, with no path-following state anywhere.
 * 2. **It is tile-local and seamless.** Two neighbouring tiles compute the same crossings from the same
 *    continuous field, so a row runs across a seam without either side knowing the other exists.
 * 3. **Its spacing tightens on steeper ground**, since a fixed height interval covers less plan distance —
 *    which is what real terracing does, and is not a rule anyone had to write.
 *
 * ## The ownership rule is the water surface's, and for the same reason
 *
 * A tile owns every grid cell whose **lower corner** falls inside it, and it reaches one grid point past its
 * own far edge so that cell can be completed. Taking only the points inside the tile left the straddling cell
 * drawn by neither side — measured as a visible gap at every seam in the rivers. Letting the spare row and
 * column own cells of their own drew the seam twice, which under bloom is twice as bright.
 *
 * ## Marching squares, and the one ambiguous case
 *
 * Per cell and per level, the crossings on the four edges are found and joined. Two crossings make one
 * segment. Four crossings mean a saddle, where two pairings are equally valid and neither is more correct —
 * the choice here is fixed and consistent rather than clever, because an inconsistent choice would flip
 * between neighbouring cells and tear the curve.
 *
 * Contains no Three.js and no DOM access, so it is fully unit testable.
 */

/** Floats per segment: two endpoints, each carrying the position and the tint that colours it. */
const STRIDE = 8;

/**
 * Build a contour collector.
 *
 * @param {object} options
 * @param {number} options.levelSpacing - Height between contour levels, in world units.
 * @param {number} options.grid - Spacing of the sampling lattice. Finer means smoother curves and more
 *   segments; the terrain's own lattice spacing is the natural choice, since detail below it is not drawn.
 * @returns {(region: object, hooks: object) => {data: Array<number>, count: number}}
 */
export function createContourLines({ levelSpacing, grid }) {
  if (!(levelSpacing > 0)) throw new Error('createContourLines: levelSpacing must be positive');
  if (!(grid > 0)) throw new Error('createContourLines: grid must be positive');

  /**
   * @param {{originX: number, originZ: number, size: number}} region
   * @param {object} hooks
   * @param {(x: number, z: number) => number} hooks.valueAt - The field being contoured. This is the biome's
   *   *own* smooth elevation, never the blended surface: the blend is a weighted sum, so contouring it would
   *   make the rows swing about wherever a neighbouring biome lifted the ground.
   * @param {(x: number, z: number) => number} hooks.groundAt - The **drawn** surface, which every vertex is
   *   lifted onto. Contouring one field and drawing on another is the whole trick — the pattern comes from
   *   the vineyard's own hillside, and the geometry sits on what is actually on screen.
   * @param {(level: number) => number|null} hooks.tintForLevel - Colour fraction for a level index, or null
   *   to skip that level entirely. This is what lets one pass draw both retaining walls and vine rows.
   * @param {(x: number, z: number, slope: number) => boolean} hooks.allowAt - Whether a cell may be drawn.
   *   **The slope is handed in rather than looked up**, because it is free here: it comes from the four corner
   *   values already sampled to find the crossings. A caller that sampled the field again to measure its own
   *   slope would be paying three extra noise evaluations per cell for a number this function already has.
   * @param {(x: number, z: number) => boolean} [hooks.allowPointAt] - Whether a single grid *point* is clear.
   *   A cell is drawn only when all four of its corners are, which puts a full cell of margin around whatever
   *   this refuses. That margin is the point: it exists for tests that leak, and the cross-biome water clearance
   *   is one — it probes a centre and a *ring*, so a shoreline can pass between the two unseen. Evaluated
   *   lazily and memoised per point, so only cells that pass the cheap gate pay for it.
   * @param {number} [hooks.lift] - How far above the ground the lines sit, to avoid fighting the terrain
   *   wireframe for the same depth.
   * @returns {{data: Array<number>, count: number}}
   */
  return function collectContours(region, { valueAt, groundAt, tintForLevel, allowAt, allowPointAt, lift = 0 }) {
    /*
      A tile owns every cell whose lower corner falls inside it, and reaches one grid point past its far edge
      so that cell can be completed. See the note at the top of this file.
    */
    const first = { x: Math.ceil(region.originX / grid), z: Math.ceil(region.originZ / grid) };
    const farX = region.originX + region.size;
    const farZ = region.originZ + region.size;

    /*
      When the far edge lands exactly on a grid line, that corner belongs to the *next* tile — otherwise both
      would draw the same cell. Tile size is not a multiple of the grid today, so this is a guard against a
      future change to either number rather than a live case.
    */
    const lastOwned = {
      x: Math.floor(farX / grid) * grid === farX ? Math.floor(farX / grid) - 1 : Math.floor(farX / grid),
      z: Math.floor(farZ / grid) * grid === farZ ? Math.floor(farZ / grid) - 1 : Math.floor(farZ / grid),
    };

    const columns = lastOwned.x - first.x + 2;
    const rows = lastOwned.z - first.z + 2;
    if (columns <= 1 || rows <= 1) return { data: [], count: 0 };

    /*
      The field at every grid point, row-major. A flat typed array rather than objects: this is a thousand
      entries per tile and the allocation shows up, which is the same call the water surface made.
    */
    const value = new Float64Array(columns * rows);

    for (let ix = 0; ix < columns; ix += 1) {
      const x = (first.x + ix) * grid;

      for (let iz = 0; iz < rows; iz += 1) {
        value[ix * rows + iz] = valueAt(x, (first.z + iz) * grid);
      }
    }

    /*
      Whether each grid point is clear, worked out on demand: 0 not yet asked, 1 yes, 2 no.

      Lazy because the answer can be the most expensive question in the pass — for a vineyard it consults every
      other biome's reservation — and most of a region never reaches it, having already failed the slope band or
      the planted field.
    */
    const pointState = allowPointAt ? new Uint8Array(columns * rows) : null;

    const pointAllows = (ix, iz) => {
      if (pointState === null) return true;

      const index = ix * rows + iz;
      if (pointState[index] === 0) {
        pointState[index] = allowPointAt((first.x + ix) * grid, (first.z + iz) * grid) ? 1 : 2;
      }

      return pointState[index] === 1;
    };

    const data = [];

    /** One endpoint: position lifted onto the drawn ground, plus the tint that colours it. */
    const push = (x, z, tint) => {
      data.push(x, groundAt(x, z) + lift, z, tint);
    };

    /*
      Crossings on a cell's four edges, in a fixed order — bottom, top, left, right. Reused between cells so
      the inner loop allocates nothing.
    */
    const crossX = new Float64Array(4);
    const crossZ = new Float64Array(4);

    for (let ix = 0; ix < columns - 1; ix += 1) {
      const x0 = (first.x + ix) * grid;
      const x1 = x0 + grid;

      for (let iz = 0; iz < rows - 1; iz += 1) {
        const z0 = (first.z + iz) * grid;
        const z1 = z0 + grid;

        const v00 = value[ix * rows + iz];
        const v10 = value[(ix + 1) * rows + iz];
        const v01 = value[ix * rows + iz + 1];
        const v11 = value[(ix + 1) * rows + iz + 1];

        const lo = Math.min(v00, v10, v01, v11);
        const hi = Math.max(v00, v10, v01, v11);

        const firstLevel = Math.ceil(lo / levelSpacing);
        const lastLevel = Math.floor(hi / levelSpacing);
        if (lastLevel < firstLevel) continue;

        /*
          The gate is checked only once a level is known to cross this cell, and at the cell centre rather
          than per corner. Most of a region is either too flat or too steep to plant, so rejecting late costs
          nothing and rejecting per corner would cost four times as much for a cell fifty units across.

          The cell's own slope goes with it, from the corner values already in hand — the central difference
          across the cell, which is the gradient of the same bilinear patch the crossings are found on.
        */
        const slope = Math.hypot((v10 + v11 - v00 - v01) / (2 * grid), (v01 + v11 - v00 - v10) / (2 * grid));

        if (!allowAt(x0 + grid / 2, z0 + grid / 2, slope)) continue;

        /*
          And every corner clear, which puts a whole cell of margin around whatever the point test refuses.
          Checking the cell centre alone was not enough: a crossing can sit anywhere on the cell boundary, up to
          35 units from that centre, and the water clearance probes a centre plus a ring — so a shoreline slipped
          between the two and rows were drawn across a lake. That is the fifth time this test's shape has caught
          somebody out, and the answer is the same every time: more, smaller, overlapping probes.
        */
        if (
          !pointAllows(ix, iz) ||
          !pointAllows(ix + 1, iz) ||
          !pointAllows(ix, iz + 1) ||
          !pointAllows(ix + 1, iz + 1)
        ) {
          continue;
        }

        for (let level = firstLevel; level <= lastLevel; level += 1) {
          const tint = tintForLevel(level);
          if (tint === null) continue;

          const height = level * levelSpacing;
          let found = 0;

          // Bottom edge, running +x.
          if (v00 < height !== v10 < height) {
            crossX[found] = x0 + (grid * (height - v00)) / (v10 - v00);
            crossZ[found] = z0;
            found += 1;
          }

          // Top edge, running +x.
          if (v01 < height !== v11 < height) {
            crossX[found] = x0 + (grid * (height - v01)) / (v11 - v01);
            crossZ[found] = z1;
            found += 1;
          }

          // Left edge, running +z.
          if (v00 < height !== v01 < height) {
            crossX[found] = x0;
            crossZ[found] = z0 + (grid * (height - v00)) / (v01 - v00);
            found += 1;
          }

          // Right edge, running +z.
          if (v10 < height !== v11 < height) {
            crossX[found] = x1;
            crossZ[found] = z0 + (grid * (height - v10)) / (v11 - v10);
            found += 1;
          }

          if (found < 2) continue;

          if (found === 2) {
            push(crossX[0], crossZ[0], tint);
            push(crossX[1], crossZ[1], tint);
            continue;
          }

          /*
            Four crossings: a saddle, where the curve passes through the cell twice and two pairings are
            equally valid. **The choice is fixed rather than clever.** Joining bottom-to-left and
            top-to-right every time keeps neighbouring cells consistent; deciding per cell on the corner
            values would flip the pairing across a saddle and tear the curve in half.
          */
          push(crossX[0], crossZ[0], tint);
          push(crossX[2], crossZ[2], tint);
          push(crossX[1], crossZ[1], tint);
          push(crossX[3], crossZ[3], tint);
        }
      }
    }

    return { data, count: data.length / STRIDE };
  };
}

/** Floats per segment in a contour's `data`. Exported so the emitter and its counter cannot disagree. */
export { STRIDE as CONTOUR_FLOATS_PER_SEGMENT };
